import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BackupService } from '../services/backupService';
import {
  ProgressArchiveService,
  ProgressArchiveListItem,
  ProgressArchiveDetail,
} from '../services/progressArchiveService';
import { progressService } from '../services/progressService';
import { useDialog } from '../context/DialogContext';
import { useStepWorkspace } from '../context/StepWorkspaceContext';
import styles from '../styles/stepWorkArchiveTab.module.css';

interface StepWorkArchiveTabProps {
  user?: { email?: string };
}

function defaultSaveName(): string {
  return `Before workshop – ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function buildWorkSummary(
  gettingStartedComplete: boolean | undefined,
  completedCount: number,
  hasData: boolean
): string {
  if (!hasData) {
    return 'No step work saved yet';
  }
  const parts: string[] = [];
  if (gettingStartedComplete) {
    parts.push('Getting Started done');
  } else {
    parts.push('Getting Started started');
  }
  if (completedCount > 0) {
    parts.push(`${completedCount} of 12 steps completed`);
  } else {
    parts.push('working through the steps');
  }
  return parts.join(' · ');
}

const StepWorkArchiveTab: React.FC<StepWorkArchiveTabProps> = ({ user }) => {
  const { showAlert, showConfirm } = useDialog();
  const { notifyStepWorkspaceChanged } = useStepWorkspace();
  const [loading, setLoading] = useState(true);
  const [archives, setArchives] = useState<ProgressArchiveListItem[]>([]);
  const [archiveCount, setArchiveCount] = useState(0);
  const [maxArchives, setMaxArchives] = useState(5);
  const [hasActiveWork, setHasActiveWork] = useState(false);
  const [activeSummary, setActiveSummary] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveName, setSaveName] = useState(defaultSaveName);
  const [showSaveForm, setShowSaveForm] = useState(false);

  const [viewArchive, setViewArchive] = useState<ProgressArchiveDetail | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [listResult, hasData] = await Promise.all([
        ProgressArchiveService.listArchives(),
        BackupService.hasCurrentData('SAA', '12-steps'),
      ]);
      setArchives(listResult.archives);
      setArchiveCount(listResult.count);
      setMaxArchives(listResult.max);
      setHasActiveWork(hasData);

      const gettingStarted = await progressService.getStepProgress('SAA', '12-steps', 0).catch(() => null);
      const gsComplete = !!gettingStarted?.completed;

      let completedCount = 0;
      if (hasData) {
        const allSteps = await progressService.getAllStepProgress('SAA', '12-steps');
        completedCount = Object.values(allSteps).filter((s) => s?.completed).length;
      }
      setActiveSummary(buildWorkSummary(gsComplete, completedCount, hasData));
    } catch (err) {
      console.error('Error loading archives:', err);
      setError('Could not load your step work. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const atArchiveLimit = archiveCount >= maxArchives;
  const userArchives = archives.filter((a) => !a.is_auto_archive);
  const autoArchives = archives.filter((a) => a.is_auto_archive);

  const openSaveForm = () => {
    if (!hasActiveWork) {
      showAlert({
        title: 'Nothing to save yet',
        message: 'You do not have any step work in Recovery Commons yet. When you do, come back here before starting again — we will save a copy for you.',
      });
      return;
    }
    if (atArchiveLimit) {
      showAlert({
        title: 'Saved copy limit reached',
        message: `You can keep up to ${maxArchives} named saves. Delete one from the list below to save another.`,
      });
      return;
    }
    setSaveName(defaultSaveName());
    setShowSaveForm(true);
  };

  const handleStartOver = async () => {
    const name = saveName.trim();
    if (!name) {
      setError('Please enter a name for this saved copy.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await ProgressArchiveService.createArchive({
        archive_name: name,
        clear_active: true,
      });
      setShowSaveForm(false);
      await loadData();
      notifyStepWorkspaceChanged();
      showAlert({
        title: 'You are ready for a fresh start',
        message: `Your prior step work is saved as "${name}". Your steps are reset — head to Getting Started when you are ready.`,
        variant: 'success',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not save your work. Please try again.';
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleViewArchive = async (archiveId: string) => {
    setViewLoading(true);
    setViewArchive(null);
    try {
      const detail = await ProgressArchiveService.getArchive(archiveId);
      setViewArchive(detail);
    } catch (err) {
      showAlert({
        title: 'Could not open saved copy',
        message: err instanceof Error ? err.message : 'Please try again.',
        variant: 'danger',
      });
    } finally {
      setViewLoading(false);
    }
  };

  const handleRestoreArchive = (item: ProgressArchiveListItem) => {
    showConfirm({
      title: 'Switch back to this saved work?',
      message: `Your current step work will be replaced with "${item.archive_name}". If you have unsaved work now, we will save a copy automatically first.`,
      confirmText: 'Switch to this copy',
      variant: 'warning',
      onConfirm: async () => {
        setBusy(true);
        setError(null);
        try {
          await ProgressArchiveService.restoreArchive(item.archive_id);
          await loadData();
          notifyStepWorkspaceChanged();
          showAlert({
            title: 'Saved work restored',
            message: `"${item.archive_name}" is now your active step work.`,
            variant: 'success',
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Could not restore. Please try again.';
          setError(message);
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const handleDeleteArchive = (item: ProgressArchiveListItem) => {
    showConfirm({
      title: 'Delete this saved copy?',
      message: `Permanently delete "${item.archive_name}"? This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        setBusy(true);
        setError(null);
        try {
          await ProgressArchiveService.deleteArchive(item.archive_id);
          if (viewArchive?.archive_id === item.archive_id) {
            setViewArchive(null);
          }
          await loadData();
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Delete failed.';
          setError(message);
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const renderArchiveRow = (item: ProgressArchiveListItem) => (
    <li key={item.archive_id} className={styles.archiveItem}>
      <div className={styles.archiveInfo}>
        <div className={styles.archiveName}>
          {item.archive_name}
          {item.is_auto_archive && <span className={styles.autoTag}>Saved automatically</span>}
        </div>
        <div className={styles.archiveMeta}>
          {ProgressArchiveService.formatDate(item.created_at)} ·{' '}
          {ProgressArchiveService.formatSummary(item.summary)}
        </div>
      </div>
      <div className={styles.archiveActions}>
        <button type="button" className={styles.linkButton} onClick={() => handleViewArchive(item.archive_id)} disabled={busy}>
          Details
        </button>
        <button type="button" className={styles.linkButton} onClick={() => handleRestoreArchive(item)} disabled={busy}>
          Switch to this
        </button>
        <button type="button" className={styles.linkButtonDanger} onClick={() => handleDeleteArchive(item)} disabled={busy}>
          Delete
        </button>
      </div>
    </li>
  );

  if (!user) {
    return (
      <div className={styles.emptyState}>
        <p>Sign in to save your step work and start over with a clean workspace.</p>
      </div>
    );
  }

  if (loading) {
    return <div className={styles.loading}>Loading…</div>;
  }

  return (
    <div className={styles.container}>
      {error && <div className={styles.error}>{error}</div>}

      <section className={styles.hero}>
        <h3 className={styles.heroTitle}>Working the steps again?</h3>
        <p className={styles.heroLead}>
          Many people come back for a workshop or a new round in recovery. If you already have step work in
          Recovery Commons, save it here first — we keep a copy for you, then reset your steps so you can
          begin fresh.
        </p>

        {hasActiveWork ? (
          <>
            <div className={styles.currentWork}>
              <span className={styles.currentWorkLabel}>Your current step work</span>
              <span className={styles.currentWorkSummary}>{activeSummary}</span>
            </div>

            {!showSaveForm ? (
              <button
                type="button"
                className={styles.heroButton}
                onClick={openSaveForm}
                disabled={busy || atArchiveLimit}
              >
                Save my work &amp; start over
              </button>
            ) : (
              <div className={styles.saveForm}>
                <label className={styles.label} htmlFor="save-name">
                  Name this saved copy
                </label>
                <p className={styles.labelHint}>So you can find it later — e.g. &ldquo;Before 2026 workshop&rdquo;</p>
                <input
                  id="save-name"
                  type="text"
                  className={styles.textInput}
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  maxLength={120}
                  disabled={busy}
                />
                <ul className={styles.whatHappens}>
                  <li>We save everything you have done so far under this name</li>
                  <li>Your steps reset to blank — ready for a new round</li>
                  <li>You can switch back to this copy anytime from the list below</li>
                </ul>
                <div className={styles.saveFormActions}>
                  <button
                    type="button"
                    className={styles.heroButton}
                    onClick={handleStartOver}
                    disabled={busy}
                  >
                    {busy ? 'Saving…' : 'Save my work & start over'}
                  </button>
                  <button
                    type="button"
                    className={styles.textButton}
                    onClick={() => setShowSaveForm(false)}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {atArchiveLimit && !showSaveForm && (
              <p className={styles.heroHint}>
                You have {maxArchives} saved copies. Delete one below to save another.
              </p>
            )}
          </>
        ) : (
          <div className={styles.readyBlock}>
            <p className={styles.readyMessage}>
              {archives.length > 0
                ? 'Your steps are clear — you are ready to begin a new round.'
                : 'Your steps are clear. When you have step work to save, come here before your next round.'}
            </p>
            <Link to="/saa/12-steps/getting-started" className={styles.heroButton}>
              Go to Getting Started
            </Link>
          </div>
        )}
      </section>

      {archives.length > 0 && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>Previously saved step work</h4>
          <p className={styles.sectionIntro}>
            {hasActiveWork
              ? 'After you start over, your prior work appears here. You can switch back anytime.'
              : 'Switch back to an earlier round, or delete copies you no longer need.'}
          </p>

          {userArchives.length > 0 && (
            <ul className={styles.archiveList}>{userArchives.map(renderArchiveRow)}</ul>
          )}

          {autoArchives.length > 0 && (
            <>
              {userArchives.length > 0 && (
                <p className={styles.autoSectionLabel}>Automatic saves</p>
              )}
              <ul className={styles.archiveList}>{autoArchives.map(renderArchiveRow)}</ul>
            </>
          )}

          <p className={styles.limitNote}>
            Up to {maxArchives} named saves ({archiveCount} used)
          </p>
        </section>
      )}

      {(viewLoading || viewArchive) && (
        <div className={styles.viewOverlay} onClick={() => !viewLoading && setViewArchive(null)}>
          <div className={styles.viewPanel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.viewHeader}>
              <h4>{viewArchive?.archive_name || 'Saved copy'}</h4>
              <button type="button" className={styles.closeView} onClick={() => setViewArchive(null)} aria-label="Close">
                ×
              </button>
            </div>
            {viewLoading ? (
              <p className={styles.viewBody}>Loading…</p>
            ) : viewArchive ? (
              <div className={styles.viewBody}>
                <p className={styles.viewMeta}>
                  Saved {ProgressArchiveService.formatDate(viewArchive.created_at)} ·{' '}
                  {ProgressArchiveService.formatSummary(viewArchive.summary)}
                </p>
                {viewArchive.summary?.completed_step_numbers?.length ? (
                  <p>Completed steps: {viewArchive.summary.completed_step_numbers.join(', ')}</p>
                ) : null}
                {viewArchive.summary?.has_inventories && (
                  <p>Inventories included ({viewArchive.summary.inventory_count})</p>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default StepWorkArchiveTab;
